import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { colors } from '@/assets/styles/colors';
import { Text } from '@/components/ui/text/Text';
import { SCREEN_PREFERENCE_CODES } from '@/routes';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import type { SimpleFilter } from '@/components/ui/filters/FilterDropdown';
import {
  securityGroupingListService,
  securityGroupingStatusService,
  type SecurityGroupItem,
  type SecurityGroupStatusFilter,
  SECURITY_GROUPING_LIST_REFRESH_EVENT,
  SECURITY_GROUPING_DEFAULT_PAGE_SIZE,
} from '@/api/domains/securityGrouping';
import { useUI } from '@/hooks/ui/useUI';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { formatToDdMmYyyy } from '@/utils';


const SECURITY_GROUPING_CREATE_PANEL_KEY = 'CreateSecurityGroupingFormPanel' as const;
const SECURITY_GROUPING_EDIT_PANEL_KEY = 'EditSecurityGroupingFormPanel' as const;

interface SecurityGroupFiltersState {
  status: SecurityGroupStatusFilter;
}

export interface UseSecurityGroupingListResult {
  rows: SecurityGroupItem[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  filters: SecurityGroupFiltersState;
  setFilters: React.Dispatch<React.SetStateAction<SecurityGroupFiltersState>>;
  filterConfig: SimpleFilter[];
  columns: TableColumn<SecurityGroupItem>[];
  actions: TableAction<SecurityGroupItem>[];
  onSearch: (term: string) => void;
  onSortChange: (key: string, direction: 'asc' | 'desc') => void;
  loading: boolean;
  openCreatePanel: () => void;
}

export function useSecurityGroupingList(): UseSecurityGroupingListResult {
  const { t: translate } = useTranslation();
  const { showApiError, showSuccess, openModal, openSidePanel } = useUI();
  const { isLoaded, canView, canEdit, canDelete } = usePagePermissions();

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<SecurityGroupFiltersState>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<SecurityGroupItem> | null> => {
      const response = await securityGroupingListService.list(
        {
          page: params.page,
          pageSize: params.pageSize,
          searchText: params.searchText,
          status: params.filters.status,
          orderBy: params.sort?.key,
          orderDirection: params.sort?.direction,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code === 'ABORTED') return null;
        showApiError(response, translate('securityGrouping:messages.loadError'));
        return null;
      }

      return { items: response.data.items, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
    refetch,
  } = usePaginatedList<SecurityGroupItem, SecurityGroupFiltersState>({
    fetchFn,
    initialFilters: { status: 'all' as SecurityGroupStatusFilter },
    initialPageSize: SECURITY_GROUPING_DEFAULT_PAGE_SIZE,
    orderByMap: {
      id: 'id',
      code: 'code',
      name: 'groupName',
      createdDate: 'createdAt',
      status: 'enabled',
    },
    enabled: isLoaded && canView,
    screenCode: SCREEN_PREFERENCE_CODES.AGRUPACIONES_GROUPINGS,
  });

  // ── List refresh event (dispatched after successful creation) ─────────────
  useEffect(() => {
    const handleRefresh = () => refetch();
    window.addEventListener(SECURITY_GROUPING_LIST_REFRESH_EVENT, handleRefresh);
    return () => {
      window.removeEventListener(SECURITY_GROUPING_LIST_REFRESH_EVENT, handleRefresh);
    };
  }, [refetch]);

  const filterConfig: SimpleFilter[] = useMemo(() => [
    {
      key: 'status',
      label: translate('securityGrouping:filters.status'),
      options: [
        { value: 'all', label: translate('securityGrouping:filters.all') },
        { value: 'enabled', label: translate('securityGrouping:filters.enabled') },
        { value: 'disabled', label: translate('securityGrouping:filters.disabled') },
      ],
    },
  ], [translate]);

  const columns: TableColumn<SecurityGroupItem>[] = useMemo(
    () => [
      {
        key: 'id',
        header: translate('securityGrouping:fields.id'),
        render: (row: SecurityGroupItem) => row.id,
        width: 80,
        sortable: true,
      },
      {
        key: 'code',
        header: translate('securityGrouping:fields.code'),
        render: (row: SecurityGroupItem) => row.code,
        sortable: true,
      },
      {
        key: 'name',
        header: translate('securityGrouping:fields.name'),
        render: (row: SecurityGroupItem) => row.name,
        sortable: true,
      },
      {
        key: 'createdDate',
        header: translate('securityGrouping:fields.createdDate'),
        render: (row: SecurityGroupItem) => formatToDdMmYyyy(row.createdDate) || '-',
        sortable: true,
      },
      {
        key: 'status',
        header: translate('securityGrouping:fields.status'),
        sortable: true,
        render: (row: SecurityGroupItem) => (
          <Text
            variant="body-regular"
            style={{
              color: row.isEnabled ? colors.feedback.success400 : colors.grays.neutral99,
              fontWeight: 500,
            }}
          >
            {row.isEnabled
              ? translate('securityGrouping:status.enabled')
              : translate('securityGrouping:status.disabled')}
          </Text>
        ),
      },
    ],
    [translate],
  );

  const openCreatePanel = useCallback(() => {
    openSidePanel({
      component: SECURITY_GROUPING_CREATE_PANEL_KEY,
      title: translate('securityGrouping:panel.createTitle'),
      props: {},
    });
  }, [openSidePanel, translate]);

  const openEditPanel = useCallback((row: SecurityGroupItem) => {
    openSidePanel({
      component: SECURITY_GROUPING_EDIT_PANEL_KEY,
      title: translate('securityGrouping:panel.editTitle'),
      props: { groupId: row.id, initialData: row },
    });
  }, [openSidePanel, translate]);

  const handleToggleStatus = useCallback((row: SecurityGroupItem, enabled: boolean) => {
    openModal('ConfirmModal', {
      variant: enabled ? 'success' : 'alert',
      title: translate(enabled
        ? 'securityGrouping:modal.enableTitle'
        : 'securityGrouping:modal.disableTitle'),
      bodyText: translate(enabled
        ? 'securityGrouping:modal.enableBody'
        : 'securityGrouping:modal.disableBody'),
      confirmText: translate('securityGrouping:modal.confirmText'),
      cancelText: translate('securityGrouping:modal.cancelText'),
      onConfirm: async () => {
        const response = await securityGroupingStatusService.updateStatus(row.id, enabled);
        if (response.success) {
          showSuccess(translate(enabled
            ? 'securityGrouping:messages.enableSuccess'
            : 'securityGrouping:messages.disableSuccess'));
          refetch();
        } else {
          showApiError(response, translate('securityGrouping:messages.statusError'));
        }
      },
    });
  }, [openModal, showSuccess, showApiError, translate, refetch]);

  const actions: TableAction<SecurityGroupItem>[] = useMemo(
    () => [
      {
        id: 'edit',
        label: translate('common:tableActions.edit'),
        icon: 'edit',
        hidden: () => !canEdit,
        onClick: (row) => openEditPanel(row),
      },
      {
        id: 'disable',
        label: translate('common:tableActions.disable'),
        icon: 'close',
        hidden: (row) => !canDelete || !row.isEnabled,
        onClick: (row) => handleToggleStatus(row, false),
        variant: 'danger',
      },
      {
        id: 'enable',
        label: translate('common:tableActions.enable'),
        icon: 'checkCircle',
        hidden: (row) => !canDelete || row.isEnabled,
        onClick: (row) => handleToggleStatus(row, true),
      },
    ],
    [translate, canEdit, canDelete, openEditPanel, handleToggleStatus],
  );

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    filters,
    setFilters,
    filterConfig,
    columns,
    actions,
    onSearch,
    onSortChange,
    loading,
    openCreatePanel,
  };
}
